/**
 * WordPress dependencies
 */
import {
	EditorSettings,
	SETTINGS_DEFAULTS,
} from '@safe-wordpress/block-editor';
import { _x } from '@safe-wordpress/i18n';

const EXPERIMENTAL_FEATURES: ExperimentalFeatures = {
	appearanceTools: false,
	useRootPaddingAwareAlignments: true,
	border: {
		color: true,
		radius: true,
		style: true,
		width: true,
	},
	color: {
		background: true,
		button: true,
		caption: true,
		customDuotone: true,
		defaultDuotone: true,
		defaultGradients: true,
		defaultPalette: true,
		duotone: {
			default: [
				{
					name: _x( 'Dark grayscale', 'text', 'nelio-ab-testing' ),
					colors: [ '#000000', '#7f7f7f' ],
					slug: 'dark-grayscale',
				},
				{
					name: _x( 'Grayscale', 'text', 'nelio-ab-testing' ),
					colors: [ '#000000', '#ffffff' ],
					slug: 'grayscale',
				},
				{
					name: _x( 'Purple and yellow', 'text', 'nelio-ab-testing' ),
					colors: [ '#8c00b7', '#fcff41' ],
					slug: 'purple-yellow',
				},
				{
					name: _x( 'Blue and red', 'text', 'nelio-ab-testing' ),
					colors: [ '#000097', '#ff4747' ],
					slug: 'blue-red',
				},
				{
					name: _x( 'Midnight', 'text', 'nelio-ab-testing' ),
					colors: [ '#000000', '#00a5ff' ],
					slug: 'midnight',
				},
				{
					name: _x(
						'Magenta and yellow',
						'text',
						'nelio-ab-testing'
					),
					colors: [ '#c7005a', '#fff278' ],
					slug: 'magenta-yellow',
				},
				{
					name: _x( 'Purple and green', 'text', 'nelio-ab-testing' ),
					colors: [ '#a60072', '#67ff66' ],
					slug: 'purple-green',
				},
				{
					name: _x( 'Blue and orange', 'text', 'nelio-ab-testing' ),
					colors: [ '#1900d8', '#ffa96b' ],
					slug: 'blue-orange',
				},
			],
			theme: [],
		},
		gradients: {
			default: [
				{
					name: _x(
						'Vivid cyan blue to vivid purple',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgba(6,147,227,1) 0%,rgb(155,81,224) 100%)',
					slug: 'vivid-cyan-blue-to-vivid-purple',
				},
				{
					name: _x(
						'Light green cyan to vivid green cyan',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgb(122,220,180) 0%,rgb(0,208,130) 100%)',
					slug: 'light-green-cyan-to-vivid-green-cyan',
				},
				{
					name: _x(
						'Luminous vivid amber to luminous vivid orange',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgba(252,185,0,1) 0%,rgba(255,105,0,1) 100%)',
					slug: 'luminous-vivid-amber-to-luminous-vivid-orange',
				},
				{
					name: _x(
						'Luminous vivid orange to vivid red',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgba(255,105,0,1) 0%,rgb(207,46,46) 100%)',
					slug: 'luminous-vivid-orange-to-vivid-red',
				},
				{
					name: _x(
						'Very light gray to cyan bluish gray',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgb(238,238,238) 0%,rgb(169,184,195) 100%)',
					slug: 'very-light-gray-to-cyan-bluish-gray',
				},
				{
					name: _x(
						'Cool to warm spectrum',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgb(74,234,220) 0%,rgb(151,120,209) 20%,rgb(207,42,186) 40%,rgb(238,44,130) 60%,rgb(251,105,98) 80%,rgb(254,248,76) 100%)',
					slug: 'cool-to-warm-spectrum',
				},
				{
					name: _x(
						'Blush light purple',
						'text',
						'nelio-ab-testing'
					),
					gradient:
						'linear-gradient(135deg,rgb(255,206,236) 0%,rgb(152,150,240) 100%)',
					slug: 'blush-light-purple',
				},
				{
					name: _x( 'Blush bordeaux', 'text', 'nelio-ab-testing' ),
					gradient:
						'linear-gradient(135deg,rgb(254,205,165) 0%,rgb(254,45,45) 50%,rgb(107,0,62) 100%)',
					slug: 'blush-bordeaux',
				},
				{
					name: _x( 'Luminous dusk', 'text', 'nelio-ab-testing' ),
					gradient:
						'linear-gradient(135deg,rgb(255,203,112) 0%,rgb(199,81,192) 50%,rgb(65,88,208) 100%)',
					slug: 'luminous-dusk',
				},
				{
					name: _x( 'Pale ocean', 'text', 'nelio-ab-testing' ),
					gradient:
						'linear-gradient(135deg,rgb(255,245,203) 0%,rgb(182,227,212) 50%,rgb(51,167,181) 100%)',
					slug: 'pale-ocean',
				},
				{
					name: _x( 'Electric grass', 'text', 'nelio-ab-testing' ),
					gradient:
						'linear-gradient(135deg,rgb(202,248,128) 0%,rgb(113,206,126) 100%)',
					slug: 'electric-grass',
				},
				{
					name: _x( 'Midnight', 'text', 'nelio-ab-testing' ),
					gradient:
						'linear-gradient(135deg,rgb(2,3,129) 0%,rgb(40,116,252) 100%)',
					slug: 'midnight',
				},
			],
			theme: [
				{
					slug: 'gradient-1',
					gradient:
						'linear-gradient(to bottom, #cfcabe 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft beige to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-2',
					gradient:
						'linear-gradient(to bottom, #C2A990 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft sandstone to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-3',
					gradient:
						'linear-gradient(to bottom, #D8613C 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft rust to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-4',
					gradient:
						'linear-gradient(to bottom, #B1C5A4 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft sage to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-5',
					gradient:
						'linear-gradient(to bottom, #B5BDBC 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft mint to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-6',
					gradient:
						'linear-gradient(to bottom, #A4A4A4 0%, #F9F9F9 100%)',
					name: _x(
						'Vertical soft pewter to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-7',
					gradient:
						'linear-gradient(to bottom, #cfcabe 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard beige to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-8',
					gradient:
						'linear-gradient(to bottom, #C2A990 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard sandstone to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-9',
					gradient:
						'linear-gradient(to bottom, #D8613C 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard rust to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-10',
					gradient:
						'linear-gradient(to bottom, #B1C5A4 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard sage to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-11',
					gradient:
						'linear-gradient(to bottom, #B5BDBC 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard mint to white',
						'text',
						'nelio-ab-testing'
					),
				},
				{
					slug: 'gradient-12',
					gradient:
						'linear-gradient(to bottom, #A4A4A4 50%, #F9F9F9 50%)',
					name: _x(
						'Vertical hard pewter to white',
						'text',
						'nelio-ab-testing'
					),
				},
			],
			custom: [],
		},
		heading: true,
		link: true,
		palette: {
			default: [
				{
					name: _x( 'Black', 'text', 'nelio-ab-testing' ),
					slug: 'black',
					color: '#000000',
				},
				{
					name: _x( 'Cyan bluish gray', 'text', 'nelio-ab-testing' ),
					slug: 'cyan-bluish-gray',
					color: '#abb8c3',
				},
				{
					name: _x( 'White', 'text', 'nelio-ab-testing' ),
					slug: 'white',
					color: '#ffffff',
				},
				{
					name: _x( 'Pale pink', 'text', 'nelio-ab-testing' ),
					slug: 'pale-pink',
					color: '#f78da7',
				},
				{
					name: _x( 'Vivid red', 'text', 'nelio-ab-testing' ),
					slug: 'vivid-red',
					color: '#cf2e2e',
				},
				{
					name: _x(
						'Luminous vivid orange',
						'text',
						'nelio-ab-testing'
					),
					slug: 'luminous-vivid-orange',
					color: '#ff6900',
				},
				{
					name: _x(
						'Luminous vivid amber',
						'text',
						'nelio-ab-testing'
					),
					slug: 'luminous-vivid-amber',
					color: '#fcb900',
				},
				{
					name: _x( 'Light green cyan', 'text', 'nelio-ab-testing' ),
					slug: 'light-green-cyan',
					color: '#7bdcb5',
				},
				{
					name: _x( 'Vivid green cyan', 'text', 'nelio-ab-testing' ),
					slug: 'vivid-green-cyan',
					color: '#00d084',
				},
				{
					name: _x( 'Pale cyan blue', 'text', 'nelio-ab-testing' ),
					slug: 'pale-cyan-blue',
					color: '#8ed1fc',
				},
				{
					name: _x( 'Vivid cyan blue', 'text', 'nelio-ab-testing' ),
					slug: 'vivid-cyan-blue',
					color: '#0693e3',
				},
				{
					name: _x( 'Vivid purple', 'text', 'nelio-ab-testing' ),
					slug: 'vivid-purple',
					color: '#9b51e0',
				},
			],
			theme: [],
			custom: [],
		},
		text: true,
	},
	dimensions: {
		defaultAspectRatios: true,
		aspectRatios: {
			default: [
				{
					name: _x( 'Square - 1:1', 'text', 'nelio-ab-testing' ),
					slug: 'square',
					ratio: '1',
				},
				{
					name: _x( 'Standard - 4:3', 'text', 'nelio-ab-testing' ),
					slug: '4-3',
					ratio: '4/3',
				},
				{
					name: _x( 'Portrait - 3:4', 'text', 'nelio-ab-testing' ),
					slug: '3-4',
					ratio: '3/4',
				},
				{
					name: _x( 'Classic - 3:2', 'text', 'nelio-ab-testing' ),
					slug: '3-2',
					ratio: '3/2',
				},
				{
					name: _x(
						'Classic Portrait - 2:3',
						'text',
						'nelio-ab-testing'
					),
					slug: '2-3',
					ratio: '2/3',
				},
				{
					name: _x( 'Wide - 16:9', 'text', 'nelio-ab-testing' ),
					slug: '16-9',
					ratio: '16/9',
				},
				{
					name: _x( 'Tall - 9:16', 'text', 'nelio-ab-testing' ),
					slug: '9-16',
					ratio: '9/16',
				},
			],
		},
		width: true,
		minWidth: true,
		maxWidth: true,
		height: true,
		minHeight: true,
		maxHeight: true,
		overflow: true,
	},
	shadow: {
		defaultPresets: true,
		presets: {
			default: [
				{
					name: _x( 'Natural', 'text', 'nelio-ab-testing' ),
					slug: 'natural',
					shadow: '6px 6px 9px rgba(0, 0, 0, 0.2)',
				},
				{
					name: _x( 'Deep', 'text', 'nelio-ab-testing' ),
					slug: 'deep',
					shadow: '12px 12px 50px rgba(0, 0, 0, 0.4)',
				},
				{
					name: _x( 'Sharp', 'text', 'nelio-ab-testing' ),
					slug: 'sharp',
					shadow: '6px 6px 0px rgba(0, 0, 0, 0.2)',
				},
				{
					name: _x( 'Outlined', 'text', 'nelio-ab-testing' ),
					slug: 'outlined',
					shadow: '6px 6px 0px -3px rgba(255, 255, 255, 1), 6px 6px rgba(0, 0, 0, 1)',
				},
				{
					name: _x( 'Crisp', 'text', 'nelio-ab-testing' ),
					slug: 'crisp',
					shadow: '6px 6px 0px rgba(0, 0, 0, 1)',
				},
			],
		},
	},
	defaultSpacingSizes: false,
	spacing: {
		blockGap: {
			sides: [ 'top', 'bottom', 'left', 'right' ],
		},
		padding: {
			sides: [ 'top', 'bottom', 'left', 'right' ],
		},
		margin: {
			sides: [ 'top', 'bottom', 'left', 'right' ],
		},
		defaultSpacingSizes: false,
		spacingScale: {
			default: {
				operator: '*',
				increment: 1.5,
				steps: 7,
				mediumStep: 1.5,
				unit: 'rem',
			},
		},
		spacingSizes: {
			default: [
				{
					name: _x( '2X-Small', 'text', 'nelio-ab-testing' ),
					slug: '20',
					size: '0.44rem',
				},
				{
					name: _x( 'X-Small', 'text', 'nelio-ab-testing' ),
					slug: '30',
					size: '0.67rem',
				},
				{
					name: _x( 'Small', 'text', 'nelio-ab-testing' ),
					slug: '40',
					size: '1rem',
				},
				{
					name: _x( 'Medium', 'text', 'nelio-ab-testing' ),
					slug: '50',
					size: '1.5rem',
				},
				{
					name: _x( 'Large', 'text', 'nelio-ab-testing' ),
					slug: '60',
					size: '2.25rem',
				},
				{
					name: _x( 'X-Large', 'text', 'nelio-ab-testing' ),
					slug: '70',
					size: '3.38rem',
				},
			],
			theme: [
				{
					name: '1',
					size: '1rem',
					slug: '10',
				},
				{
					name: '2',
					size: 'min(1.5rem, 2vw)',
					slug: '20',
				},
				{
					name: '3',
					size: 'min(2.5rem, 3vw)',
					slug: '30',
				},
				{
					name: '4',
					size: 'min(4rem, 5vw)',
					slug: '40',
				},
				{
					name: '5',
					size: 'min(6.5rem, 8vw)',
					slug: '50',
				},
				{
					name: '6',
					size: 'min(10.5rem, 13vw)',
					slug: '60',
				},
			],
			custom: [],
		},
	},
	typography: {
		defaultFontSizes: false,
		dropCap: true,
		fontSizes: {
			default: [
				{
					name: _x( 'Small', 'text', 'nelio-ab-testing' ),
					slug: 'small',
					size: '13px',
				},
				{
					name: _x( 'Medium', 'text', 'nelio-ab-testing' ),
					slug: 'medium',
					size: '20px',
				},
				{
					name: _x( 'Large', 'text', 'nelio-ab-testing' ),
					slug: 'large',
					size: '36px',
				},
				{
					name: _x( 'Extra Large', 'text', 'nelio-ab-testing' ),
					slug: 'x-large',
					size: '42px',
				},
			],
			theme: [
				{
					fluid: false,
					name: _x( 'Small', 'text', 'nelio-ab-testing' ),
					size: '0.9rem',
					slug: 'small',
				},
				{
					fluid: false,
					name: _x( 'Medium', 'text', 'nelio-ab-testing' ),
					size: '1.05rem',
					slug: 'medium',
				},
				{
					fluid: {
						min: '1.39rem',
						max: '1.85rem',
					},
					name: _x( 'Large', 'text', 'nelio-ab-testing' ),
					size: '1.85rem',
					slug: 'large',
				},
				{
					fluid: {
						min: '1.85rem',
						max: '2.5rem',
					},
					name: _x( 'Extra Large', 'text', 'nelio-ab-testing' ),
					size: '2.5rem',
					slug: 'x-large',
				},
				{
					fluid: {
						min: '2.5rem',
						max: '3.27rem',
					},
					name: _x( 'Extra Extra Large', 'text', 'nelio-ab-testing' ),
					size: '3.27rem',
					slug: 'xx-large',
				},
			],
			custom: [],
		},
		customFontSize: true,
		fontStyle: true,
		fontWeight: true,
		letterSpacing: true,
		lineHeight: true,
		textAlign: true,
		textDecoration: true,
		textTransform: true,
		textColumns: true,
		writingMode: true,
		whiteSpace: true,
		wordBreak: true,
		fluid: true,
		fontFamilies: {
			default: [
				{
					fontFamily: "Arial,'Helvetica Neue',Helvetica,sans-serif",
					name: 'Arial',
					slug: 'Arial',
				},
				{
					fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
					name: 'Helvetica Neue',
					slug: 'helvetica-neue',
				},
				{
					fontFamily:
						"'TimesNewRoman','Times New Roman',Times,Baskerville,Georgia,serif",
					name: 'Times New Roman',
					slug: 'times-new-roman',
				},
				{
					fontFamily:
						"'Courier New',Courier,'Lucida Sans Typewriter','Lucida Typewriter',monospace",
					name: 'Courier New',
					slug: 'courier-new',
				},
				{
					fontFamily: 'Verdana,Geneva,sans-serif',
					name: 'Verdana',
					slug: 'verdana',
				},
				{
					fontFamily: "Georgia,Times,'Times New Roman',serif",
					name: 'Georgia',
					slug: 'georgia',
				},
				{
					fontFamily:
						"Palatino,'Palatino Linotype','Palatino LT STD','Book Antiqua',Georgia,serif",
					name: 'Palatino',
					slug: 'palatino',
				},
				{
					fontFamily:
						"Garamond,Baskerville,'Baskerville Old Face','Hoefler Text','Times New Roman',serif",
					name: 'Garamond',
					slug: 'garamond',
				},
				{
					fontFamily:
						"'Trebuchet MS','Lucida Grande','Lucida Sans Unicode','Lucida Sans',Tahoma,sans-serif",
					name: 'Trebuchet MS',
					slug: 'trebuchet-ms',
				},
				{
					fontFamily: "'Arial Black','Arial Bold',Gadget,sans-serif",
					name: 'Arial Black',
					slug: 'arial-black',
				},
				{
					fontFamily:
						"Impact,Haettenschweiler,'Franklin Gothic Bold',Charcoal,'Helvetica Inserat','Bitstream Vera Sans Bold','Arial Black','sans serif'",
					name: 'Impact',
					slug: 'impact',
				},
				{
					fontFamily: 'Tahoma,Verdana,Segoe,sans-serif',
					name: 'Tahoma',
					slug: 'tahoma',
				},
			],
			theme: [],
			custom: [],
		},
	},
	layout: {
		contentSize: '620px',
		wideSize: '1280px',
	},
	background: {
		backgroundImage: true,
		backgroundSize: true,
	},
	position: {
		sides: [ 'top', 'bottom', 'left', 'right' ],

		zIndex: true,
	},
};

export type ExperimentalFeatures = {
	appearanceTools: boolean;
	useRootPaddingAwareAlignments: boolean;
	border: {
		color: boolean;
		radius: boolean;
		style: boolean;
		width: boolean;
	};
	color: {
		background: boolean;
		button: boolean;
		caption: boolean;
		customDuotone: boolean;
		defaultDuotone: boolean;
		defaultGradients: boolean;
		defaultPalette: boolean;
		duotone: {
			default: Array< Duotone >;
			theme: Array< Duotone >;
		};
		gradients: {
			default: Array< Gradient >;
			theme: Array< Gradient >;
			custom: Array< Gradient >;
		};
		heading: boolean;
		link: boolean;
		palette: {
			default: Array< Palette >;
			theme: Array< Palette >;
			custom: Array< Palette >;
		};
		text: boolean;
	};
	dimensions: {
		defaultAspectRatios: boolean;
		aspectRatios: {
			default: Array< AspectRatio >;
			theme?: Array< AspectRatio >;
		};
		aspectRatio?: string;
		width: boolean;
		minWidth: boolean;
		maxWidth: boolean;
		height: boolean;
		minHeight: boolean;
		maxHeight: boolean;
		overflow: boolean;
	};
	shadow: {
		defaultPresets: boolean;
		presets: {
			default: Array< Shadow >;
			theme?: Array< Shadow >;
			custom?: Array< Shadow >;
		};
	};
	defaultSpacingSizes?: boolean;
	disableCustomSpacingSizes?: boolean;
	spacing: {
		blockGap?: Array< string > | { sides: Array< string > };
		margin: Array< string > | { sides: Array< string > };
		padding: Array< string > | { sides: Array< string > };
		defaultSpacingSizes: boolean;
		spacingScale: {
			default: {
				operator: string;
				increment: number;
				steps: number;
				mediumStep: number;
				unit: string;
			};
		};
		spacingSizes: {
			default: Array< SpacingSize >;
			theme: Array< SpacingSize >;
			custom: Array< SpacingSize >;
		};
	};
	typography: {
		customFontSize: boolean;
		defaultFontSizes: boolean;
		dropCap: boolean;
		fontSizes: {
			default: Array< FontSize >;
			theme: Array< FontSize >;
			custom: Array< FontSize >;
		};
		fontStyle: boolean;
		fontWeight: boolean;
		letterSpacing: boolean;
		lineHeight: boolean;
		textAlign: boolean;
		textDecoration: boolean;
		textTransform: boolean;
		textColumns: boolean;
		writingMode: boolean;
		whiteSpace: boolean;
		wordBreak: boolean;
		size?: string;
		fluid:
			| boolean
			| {
					maxViewportWidth?: string;
					minFontSize?: string;
					minViewportWidth?: string;
					min?: string;
					max?: string;
			  };
		fontFamilies: {
			default: Array< FontFamily >;
			theme: Array< FontFamily >;
			custom: Array< FontFamily >;
		};
	};
	layout: {
		contentSize?: string;
		wideSize?: string;
		selfStretch?: string;
		flexSize?: string;
		columnStart?: number;
		rowStart?: number;
		columnSpan?: number;
		rowSpan?: number;
	};
	parentLayout?: {
		type?: string;
		default?: {
			type?: string;
		};
		orientation?: string;
		allowSizingOnChildren?: boolean;
		columnCount?: number;
		rowCount?: number;
	};
	background: {
		backgroundImage: boolean;
		backgroundSize: boolean;
	};
	position: {
		sides: Array< string >;
		zIndex: boolean;
	};
};

type Duotone = {
	name: string;
	colors: Array< string >;
	slug: string;
};

export type Gradient = {
	name: string;
	gradient: string;
	slug: string;
};

export type Palette = {
	name: string;
	color: string;
	slug: string;
};

type AspectRatio = {
	name: string;
	ratio: string;
	slug: string;
};

export type Shadow = {
	name: string;
	shadow: string;
	slug: string;
};

export type SpacingSize = {
	name: string;
	size: string;
	slug: string;
};

type FontSize = {
	name: string;
	size: string;
	slug: string;
	fluid?: boolean | { min: string; max: string };
};

export type FontFamily = {
	fontFace?: Array< FontFace >;
	fontFamily?: string;
	name: string;
	slug: string;
};

export type FontFace = {
	fontFamily: string;
	fontStretch?: string;
	fontStyle: string;
	fontWeight: string | number;
	src: Array< string >;
};

export type FullEditorSettings = EditorSettings & ExperimentalFeatures;

export function getEditorSettings(): FullEditorSettings {
	return { ...SETTINGS_DEFAULTS, ...EXPERIMENTAL_FEATURES };
}
